import type {
  ArchetypeId,
  InterestId,
  StrengthId,
  AmbitionId,
  LifePathId,
  AxisId,
  SelvesInput,
  SelvesState,
  ResolvedInput,
} from "./types";
import {
  INTEREST_ORDER,
  STRENGTH_ORDER,
  AMBITION_ORDER,
  LIFE_PATH_ORDER,
} from "./types";

export const INTERESTS: { id: InterestId; label: string; hint: string }[] = [
  { id: "science", label: "Science & Cosmos", hint: "Investigating natural laws, forces, and celestial phenomena." },
  { id: "art", label: "Art & Aesthetics", hint: "Expressing perception through visual form, color, and medium." },
  { id: "music", label: "Sound & Music", hint: "Harmonizing rhythms, vibrations, and acoustic structures." },
  { id: "nature", label: "Ecology & Living World", hint: "Attuned to living organisms, biomes, and planetary cycles." },
  { id: "technology", label: "Invention & Tech", hint: "Designing instruments, networks, and mechanical systems." },
  { id: "people", label: "Society & Culture", hint: "Connecting communities, language, and shared human bonds." },
  { id: "stories", label: "Narratives & Lore", hint: "Chronicling histories, myths, and preserved memory." },
  { id: "adventure", label: "Exploration & Frontier", hint: "Crossing boundaries into unmapped terrains and horizons." },
];

export const STRENGTHS: { id: StrengthId; label: string; hint: string }[] = [
  { id: "curiosity", label: "Deep Curiosity", hint: "Relentless hunger to question assumptions and probe the unknown." },
  { id: "empathy", label: "Attuned Empathy", hint: "Sensing the inner states and struggles of others with resonance." },
  { id: "discipline", label: "Focused Discipline", hint: "Steady consistency and patient mastery through routine." },
  { id: "creativity", label: "Inventive Creativity", hint: "Synthesizing unexpected connections into original forms." },
  { id: "courage", label: "Quiet Courage", hint: "Stepping toward danger or uncertainty when purpose demands it." },
  { id: "logic", label: "Structured Logic", hint: "Disentangling tangled problems into lucid, testable steps." },
  { id: "humor", label: "Levity & Wit", hint: "Finding unexpected light, paradox, and warmth in tough terrain." },
  { id: "patience", label: "Long-horizon Patience", hint: "Enduring delays calmly while slowly maturing a complex work." },
];

export const AMBITIONS: { id: AmbitionId; label: string; hint: string }[] = [
  { id: "understand", label: "Understand Fundamental Reality", hint: "Grasp why the universe behaves the way it does." },
  { id: "build", label: "Build Enduring Systems", hint: "Construct frameworks, architectures, or tools that outlast you." },
  { id: "help", label: "Shelter and Uplift Others", hint: "Reduce suffering and expand safety for vulnerable lives." },
  { id: "lead", label: "Chart Common Purpose", hint: "Rally diverse voices toward collective discovery or renewal." },
  { id: "create", label: "Bring Novel Beauty to Life", hint: "Leave behind works that alter how others perceive existence." },
  { id: "wander", label: "Roam Beyond the Edge", hint: "Witness wonders that no record has yet chronicled." },
];

export const LIFE_PATHS: { id: LifePathId; label: string; hint: string }[] = [
  { id: "rooted", label: "The Rooted Steward", hint: "Deeply planted in a beloved place, tending its soil and lineage." },
  { id: "scholar", label: "The Archival Scholar", hint: "Immersed in libraries, laboratories, and lifelong study." },
  { id: "maker", label: "The Hands-on Maker", hint: "Shaping materials, testing prototypes, and working the craft." },
  { id: "voyager", label: "The Restless Voyager", hint: "Always moving toward the next port, pass, or star cluster." },
  { id: "guide", label: "The Compassionate Guide", hint: "Walking alongside others as a mentor, teacher, or counselor." },
  { id: "founder", label: "The Bold Founder", hint: "Starting new institutions, expeditions, or creative leagues." },
];

export const AXIS_LABELS: Record<AxisId, string> = {
  curiosity: "Curiosity & Inquiry",
  connection: "Connection & Empathy",
  courage: "Courage & Risk Tolerance",
  stability: "Stability & Order",
  influence: "Influence & Scope",
};

export const ARCHETYPE_META: Record<ArchetypeId, { label: string; blurb: string }> = {
  divergent: {
    label: "A Different Life Path",
    blurb: "Near-identical cosmic laws to ours; one early human fork turned into an alternate lifetime.",
  },
  ascendant: {
    label: "An Advanced Future Civilization",
    blurb: "Far-future cosmic era where mastery over energy and data scales personal purpose to stellar heights.",
  },
  strange: {
    label: "A Radically Different World",
    blurb: "A cosmos with altered physical constants where biology and conscious life adapted in alien ways.",
  },
  unexpected: {
    label: "An Unexpected Identity",
    blurb: "A surprising role and life cadence that inverts familiar habits to reveal an unpredicted counterpart.",
  },
};

export const MAX_INTERESTS = 3;
export const MAX_STRENGTHS = 3;
export const NICKNAME_MAX = 24;

export const FICTION_NOTICE =
  "Fictional multiverse storytelling inspired by scientific ideas. These alternate selves and cosmoses are speculative literary thought experiments, not scientific discoveries, psychometric evaluations, or real alternate identities.";

export const PRIVACY_NOTE =
  "Free exploration runs entirely in your browser. If you choose to unlock the full dossier, only your name, email, report configuration code and the payment record (held securely by PayPal) are stored with your order. We never sell your data.";

export const SCIENCE_NOTE =
  "Parallel universes and many-worlds branches remain fascinating, unconfirmed theoretical hypotheses. The personas here translate real cosmological models and physics into imaginative human narratives.";

export const DISPLAY_FALLBACK_NAME = "Traveler";

export function emptyInput(): SelvesInput {
  return {
    nickname: "",
    interests: [],
    strengths: [],
    ambition: null,
    lifePath: null,
  };
}

export function emptyState(): SelvesState {
  return {
    input: emptyInput(),
    variants: [0, 0, 0, 0],
    universe: null,
  };
}

export function displayName(input: SelvesInput): string {
  const trimmed = input.nickname ? input.nickname.trim() : "";
  return trimmed.length > 0 ? trimmed : DISPLAY_FALLBACK_NAME;
}

export function randomVariant(rng: () => number = Math.random): number {
  return Math.floor(rng() * 1_000_000);
}

export function randomVariants(rng: () => number = Math.random): SelvesState["variants"] {
  return [randomVariant(rng), randomVariant(rng), randomVariant(rng), randomVariant(rng)];
}

function shuffleSample<T>(arr: readonly T[], count: number, rng: () => number): T[] {
  const pool = [...arr];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const temp = pool[i];
    pool[i] = pool[j];
    pool[j] = temp;
  }
  return pool.slice(0, count);
}

export function randomInput(rng: () => number = Math.random): SelvesInput {
  const interestCount = 1 + Math.floor(rng() * 3); // 1..3
  const strengthCount = 1 + Math.floor(rng() * 3); // 1..3

  const pickedInterests = shuffleSample(INTEREST_ORDER, interestCount, rng);
  const pickedStrengths = shuffleSample(STRENGTH_ORDER, strengthCount, rng);

  // Preserve canonical order
  const interests = INTEREST_ORDER.filter((id) => pickedInterests.includes(id));
  const strengths = STRENGTH_ORDER.filter((id) => pickedStrengths.includes(id));

  const ambition = AMBITION_ORDER[Math.floor(rng() * AMBITION_ORDER.length)];
  const lifePath = LIFE_PATH_ORDER[Math.floor(rng() * LIFE_PATH_ORDER.length)];

  return {
    nickname: "",
    interests,
    strengths,
    ambition,
    lifePath,
  };
}

/**
 * Resolve visitor answers, filling skipped fields with seeded picks.
 * Keeps provided answers in canonical order; draws 1-3 interests/strengths,
 * ambition, and lifePath when omitted.
 */
export function resolveInput(input: SelvesInput, rng: () => number): ResolvedInput {
  const filledInterests = input.interests.length === 0;
  let interests: InterestId[];
  if (filledInterests) {
    const count = 1 + Math.floor(rng() * 3); // 1..3
    const picked = shuffleSample(INTEREST_ORDER, count, rng);
    interests = INTEREST_ORDER.filter((id) => picked.includes(id));
  } else {
    // Preserve canonical order and clamp to 1..3
    const canonical = INTEREST_ORDER.filter((id) => input.interests.includes(id));
    interests = canonical.slice(0, 3);
    if (interests.length === 0) {
      interests = [INTEREST_ORDER[Math.floor(rng() * INTEREST_ORDER.length)]];
    }
  }

  const filledStrengths = input.strengths.length === 0;
  let strengths: StrengthId[];
  if (filledStrengths) {
    const count = 1 + Math.floor(rng() * 3); // 1..3
    const picked = shuffleSample(STRENGTH_ORDER, count, rng);
    strengths = STRENGTH_ORDER.filter((id) => picked.includes(id));
  } else {
    const canonical = STRENGTH_ORDER.filter((id) => input.strengths.includes(id));
    strengths = canonical.slice(0, 3);
    if (strengths.length === 0) {
      strengths = [STRENGTH_ORDER[Math.floor(rng() * STRENGTH_ORDER.length)]];
    }
  }

  const filledAmbition = input.ambition === null;
  const ambition: AmbitionId = filledAmbition
    ? AMBITION_ORDER[Math.floor(rng() * AMBITION_ORDER.length)]
    : input.ambition;

  const filledLifePath = input.lifePath === null;
  const lifePath: LifePathId = filledLifePath
    ? LIFE_PATH_ORDER[Math.floor(rng() * LIFE_PATH_ORDER.length)]
    : input.lifePath;

  return {
    interests,
    strengths,
    ambition,
    lifePath,
    filled: {
      interests: filledInterests,
      strengths: filledStrengths,
      ambition: filledAmbition,
      lifePath: filledLifePath,
    },
  };
}
